// src/hooks/useVoiceAlerts.js
// Voice readout of critical alerts via the Web Speech Synthesis API.
//
// Works completely offline — speechSynthesis is a browser API,
// no network required. Built into Chrome, Safari, Firefox, Edge.
//
// Reads aloud when:
//   - A new EXTREME or SEVERE NWS alert arrives
//   - An SOS post appears within the user's zone
//   - Disaster mode activates
//
// Does NOT read:
//   - Routine feed posts
//   - Gas station updates
//   - Staleness warnings
//
// Design: short, urgent sentences. No jargon.
// Uses a slower rate and lower pitch for clarity.
// Cancels any in-progress speech before starting a new alert.

import { useEffect, useRef, useCallback } from 'react';
import { useAlertStore }                  from '@store/alertStore';
import { useAccessibilityStore }          from '@store/accessibilityStore';
import { useAppStore }                    from '@store';
import { useDisasterMode }               from './useDisasterMode';

// Severity → spoken prefix
const SEV_PREFIX = {
  Extreme: 'EMERGENCY ALERT.',
  Severe:  'Severe weather alert.',
  Moderate:'Weather alert.',
  Minor:   'Weather notice.',
};

function speak(text, { rate = 0.85, pitch = 0.9, volume = 1 } = {}) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();  // stop anything currently playing

  const utt   = new SpeechSynthesisUtterance(text);
  utt.rate    = rate;
  utt.pitch   = pitch;
  utt.volume  = volume;
  utt.lang    = 'en-US';

  // Prefer a local voice to avoid any network dependency
  const voices = window.speechSynthesis.getVoices();
  const local  = voices.find(v => v.localService && v.lang.startsWith('en'));
  if (local) utt.voice = local;

  window.speechSynthesis.speak(utt);
}

export function useVoiceAlerts() {
  const voiceEnabled   = useAccessibilityStore(s => s.voiceAlerts);
  const nwsAlerts      = useAlertStore(s => s.nwsAlerts);
  const zone           = useAppStore(s => s.zone);
  const { active: disasterActive, alertSummary } = useDisasterMode();
  const spokenIds      = useRef(new Set());   // track what we've already read
  const prevDisaster   = useRef(false);       // track disaster mode transitions

  // Read a new NWS alert aloud
  useEffect(() => {
    if (!voiceEnabled) return;

    for (const alert of nwsAlerts) {
      const id = alert.id ?? alert.title;
      if (!id || spokenIds.current.has(id)) continue;

      const severity = alert.severity ?? 'Moderate';
      if (severity !== 'Extreme' && severity !== 'Severe') continue;

      spokenIds.current.add(id);

      const prefix  = SEV_PREFIX[severity] ?? 'Alert.';
      const event   = alert.event ?? alert.title ?? 'Severe weather';
      const areas   = alert.areas
        ? alert.areas.split(';').slice(0, 2).join(' and ')
        : 'your area';

      const text = `${prefix} ${event} in effect for ${areas}. Check the app for details.`;
      speak(text);
      break;  // read one at a time
    }
  }, [nwsAlerts, voiceEnabled]);

  // Speak when disaster mode first activates (new threat)
  useEffect(() => {
    if (!voiceEnabled) { prevDisaster.current = disasterActive; return; }
    if (disasterActive && !prevDisaster.current) {
      const event = alertSummary?.event ?? 'severe weather alert';
      speak(`Emergency. ${event} is now active. Open Eye on Storm for details.`, { rate: 0.8 });
    }
    prevDisaster.current = disasterActive;
  }, [disasterActive, alertSummary, voiceEnabled]);

  // Manual speak function — called by components for on-demand readout
  const readAloud = useCallback((text) => {
    if (!voiceEnabled && !('speechSynthesis' in window)) return;
    speak(text);
  }, [voiceEnabled]);

  // Read a feed card aloud (called when user taps the speaker icon)
  const readCard = useCallback((post) => {
    if (!('speechSynthesis' in window)) return;
    const type   = post.type?.toUpperCase() ?? 'REPORT';
    const body   = post.body ?? '';
    const loc    = post.address ?? post.zone ?? '';
    const age    = post.ts
      ? Math.round((Date.now() - post.ts) / 60000) + ' minutes ago'
      : '';
    const text = `${type} report. ${body}. ${loc ? 'Location: ' + loc + '.' : ''} ${age ? 'Posted ' + age + '.' : ''}`;
    speak(text);
  }, []);

  const stopSpeaking = useCallback(() => {
    window.speechSynthesis?.cancel();
  }, []);

  const isSpeechAvailable = 'speechSynthesis' in window;

  return { readAloud, readCard, stopSpeaking, isSpeechAvailable };
}
